import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, Upload, Shield, Eye, ArrowLeft, MoveVertical, ChevronDown, ChevronUp, Settings, Layout, Palette, Type, Image as ImageIcon, Layers, Bold, Italic, List, Link as LinkIcon, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './AdminBlogs.css';

const AdminBlogs = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    category: 'Technology',
    product: 'WhiteMatter',
    description: '',
    color: '#007AFF',
    imageFile: null,
    cardImageFile: null,
    cardImageFileName: null,
    // Template configuration
    headerSize: 'large',
    textSize: 'medium',
    imagePosition: 'top',
    // Hero image controls
    heroImageSize: 'medium',
    heroImageWidth: '100',
    heroImageHeight: 'auto',
    heroImageFit: 'cover',
    heroImageAlignment: 'center',
    // Layout controls
    layoutStyle: 'default', // default, wide, narrow, full-width
    contentWidth: 'standard', // narrow, standard, wide, full
    showTableOfContents: true,
    tocPosition: 'right', // left, right
    // Color scheme
    backgroundColor: '#FAFAFA',
    contentBackgroundColor: '#FFFFFF',
    textColor: '#1D1D1F',
    accentColor: '#0071e3',
    // Spacing
    sectionSpacing: 'medium', // compact, medium, spacious
    contentPadding: 'medium', // small, medium, large
    // Typography
    fontFamily: 'system', // system, serif, mono
    headingWeight: 'bold', // normal, semibold, bold, extrabold
    // Hero customization
    heroHeight: 'medium', // small, medium, large, xlarge
    heroOverlay: 'medium', // none, light, medium, dark
    showHeroMeta: true,
    sections: [
      { id: 1, type: 'text', title: 'Overview', content: '', fontSize: 'medium', imageFile: null, imageFileName: null, imagePosition: 'top' }
    ]
  });

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = () => {
    const token = localStorage.getItem('adminBlogToken');
    const user = localStorage.getItem('adminBlogUser');
    
    if (!token || !user) {
      navigate('/admin/blog-login');
      return;
    }
    
    setIsAuthenticated(true);
    setAdminUser(JSON.parse(user));
    setLoading(false);
    fetchBlogs();
  };

  const handleLogout = () => {
    localStorage.removeItem('adminBlogToken');
    localStorage.removeItem('adminBlogUser');
    navigate('/admin/blog-login');
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminBlogToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const fetchBlogs = async () => {
    try {
      const response = await fetch('/api/blogs');
      const data = await response.json();
      setBlogs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      setBlogs([]);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileChange = (field, file) => {
    setFormData(prev => ({
      ...prev,
      [field]: file
    }));
  };

  const addSection = () => {
    const newSection = {
      id: Date.now(),
      type: 'text',
      title: '',
      content: '',
      fontSize: 'medium',
      imageFile: null,
      imageFileName: null,
      imagePosition: 'top',
      imageOffsetX: 0, // horizontal offset for main image
      imageOffsetY: 0, // vertical offset for main image
      imageCustomWidth: 100, // width for main image
      imageSpaceBelow: 32, // margin bottom for main image
      images: [], // Array of multiple images: [{file, fileName, caption, id}]
      // Section layout controls
      sectionLayout: 'single', // single, two-column, three-column, grid
      contentAlignment: 'left', // left, center, right, justify
      sectionBackground: '#FFFFFF',
      sectionBorder: 'none', // none, light, medium, heavy
      sectionBorderColor: '#E5E5E7',
      sectionPadding: 'medium', // small, medium, large, none
      sectionMargin: 'medium', // small, medium, large, none
      sectionShadow: 'light', // none, light, medium, heavy
      sectionRadius: 'medium', // none, small, medium, large, full
      // Image size controls
      imageSize: 'medium', // small, medium, large, full, custom
      imageWidth: '100', // percentage for custom
      imageHeight: 'auto', // auto, small, medium, large, custom
      imageFit: 'cover', // cover, contain, fill, none
      imageAlignment: 'center', // left, center, right
      titleBackgroundColor: 'transparent', // background color for section title
      imageCaption: '' // caption text below image (for single image backward compatibility)
    };
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const removeSection = (id) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter(section => section.id !== id)
    }));
  };

  const moveSection = (index, direction) => {
    const newSections = [...formData.sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newSections.length) return;
    
    // Swap sections
    [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
    
    setFormData(prev => ({
      ...prev,
      sections: newSections
    }));
  };

  const updateSection = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === id ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateSectionImage = (id, file) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === id ? { ...section, imageFile: file } : section
      )
    }));
  };

  const addImageToSection = (sectionId, file) => {
    const newImage = {
      id: Date.now(),
      file: file,
      fileName: null,
      caption: '',
      imagePosition: 'top',
      offsetX: 0, // horizontal offset in pixels
      offsetY: 0, // vertical offset in pixels
      customWidth: 100, // width in percentage
      customHeight: 'auto', // height (auto or pixels)
      spaceBelow: 32, // margin bottom in pixels
      imageText: '' // text content below image
    };
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId 
          ? { ...section, images: [...(section.images || []), newImage] }
          : section
      )
    }));
  };

  const removeImageFromSection = (sectionId, imageId) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, images: section.images.filter(img => img.id !== imageId) }
          : section
      )
    }));
  };

  const updateSectionImageCaption = (sectionId, imageId, caption) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              images: section.images.map(img =>
                img.id === imageId ? { ...img, caption } : img
              )
            }
          : section
      )
    }));
  };

  const updateSectionImagePosition = (sectionId, imageId, position) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              images: section.images.map(img =>
                img.id === imageId ? { ...img, imagePosition: position } : img
              )
            }
          : section
      )
    }));
  };

  const updateImageProperty = (sectionId, imageId, property, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? {
              ...section,
              images: section.images.map(img =>
                img.id === imageId ? { ...img, [property]: value } : img
              )
            }
          : section
      )
    }));
  };

  const insertFormatting = (sectionId, format) => {
    const section = formData.sections.find(s => s.id === sectionId);
    if (!section) return;

    const textarea = document.getElementById(`content-${sectionId}`);
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = section.content.substring(start, end);
    const beforeText = section.content.substring(0, start);
    const afterText = section.content.substring(end);

    let newText = '';
    if (format === 'bold') {
      newText = beforeText + '**' + selectedText + '**' + afterText;
    } else if (format === 'italic') {
      newText = beforeText + '*' + selectedText + '*' + afterText;
    } else if (format === 'bullet') {
      const lines = selectedText.split('\n');
      const bulletedLines = lines.map(line => line.trim() ? '• ' + line.trim() : line).join('\n');
      newText = beforeText + bulletedLines + afterText;
    } else if (format === 'link') {
      const url = prompt('Enter URL:', 'https://');
      if (url) {
        const linkText = selectedText || 'link text';
        newText = beforeText + '[' + linkText + '](' + url + ')' + afterText;
      } else {
        return;
      }
    }

    updateSection(sectionId, 'content', newText);
    
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = format === 'bullet' ? start + newText.length - afterText.length : start + (format === 'bold' ? 2 : 1);
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Function to insert formatting for image text fields
  const insertImageTextFormatting = (sectionId, format, imageId = null) => {
    const section = formData.sections.find(s => s.id === sectionId);
    if (!section) return;

    const textareaId = imageId ? `imageText-${sectionId}-${imageId}` : `imageText-${sectionId}`;
    const textarea = document.getElementById(textareaId);
    if (!textarea) return;

    const currentText = imageId 
      ? (section.images.find(img => img.id === imageId)?.imageText || '')
      : (section.imageText || '');
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = currentText.substring(start, end);
    const beforeText = currentText.substring(0, start);
    const afterText = currentText.substring(end);

    let newText = '';
    if (format === 'bold') {
      newText = beforeText + '**' + selectedText + '**' + afterText;
    } else if (format === 'italic') {
      newText = beforeText + '*' + selectedText + '*' + afterText;
    } else if (format === 'bullet') {
      const lines = selectedText.split('\n');
      const bulletedLines = lines.map(line => line.trim() ? '• ' + line.trim() : line).join('\n');
      newText = beforeText + bulletedLines + afterText;
    } else if (format === 'link') {
      const url = prompt('Enter URL:', 'https://');
      if (url) {
        const linkText = selectedText || 'link text';
        newText = beforeText + '[' + linkText + '](' + url + ')' + afterText;
      } else {
        return;
      }
    }

    if (imageId) {
      updateImageProperty(sectionId, imageId, 'imageText', newText);
    } else {
      updateSection(sectionId, 'imageText', newText);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only allow submission on step 4
    if (currentStep !== totalSteps) {
      console.log('Form submission blocked - not on final step');
      return;
    }
    
    setLoading(true);

    try {
      // Upload section images first
      const sectionsWithImages = await Promise.all(
        formData.sections.map(async (section) => {
          let imageFileName = section.imageFileName;
          
          // Upload single section image if exists
          if (section.imageFile) {
            const reader = new FileReader();
            const base64Promise = new Promise((resolve) => {
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(section.imageFile);
            });
            const base64Image = await base64Promise;
            
            const uploadResponse = await fetch('/api/upload-image', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                image: base64Image, 
                filename: section.imageFile.name
              })
            });
            
            if (uploadResponse.ok) {
              const uploadData = await uploadResponse.json();
              imageFileName = uploadData.filename;
            }
          }

          // Upload multiple images if they exist
          let uploadedImages = [];
          if (section.images && section.images.length > 0) {
            uploadedImages = await Promise.all(
              section.images.map(async (img) => {
                if (img.file) {
                  const reader = new FileReader();
                  const base64Promise = new Promise((resolve) => {
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(img.file);
                  });
                  const base64Image = await base64Promise;
                  
                  const uploadResponse = await fetch('/api/upload-image', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                      image: base64Image, 
                      filename: img.file.name
                    })
                  });
                  
                  if (uploadResponse.ok) {
                    const uploadData = await uploadResponse.json();
                    return {
                      id: img.id,
                      fileName: uploadData.filename,
                      caption: img.caption,
                      imagePosition: img.imagePosition || 'top',
                      offsetX: img.offsetX || 0,
                      offsetY: img.offsetY || 0,
                      customWidth: img.customWidth || 100,
                      spaceBelow: img.spaceBelow !== undefined ? img.spaceBelow : 32,
                      imageText: img.imageText || ''
                    };
                  }
                }
                return {
                  id: img.id,
                  fileName: img.fileName,
                  caption: img.caption,
                  imagePosition: img.imagePosition || 'top',
                  offsetX: img.offsetX || 0,
                  offsetY: img.offsetY || 0,
                  customWidth: img.customWidth || 100,
                  spaceBelow: img.spaceBelow !== undefined ? img.spaceBelow : 32,
                  imageText: img.imageText || ''
                };
              })
            );
          }

          return {
            ...section,
            imageFileName: imageFileName,
            imageFile: undefined,
            images: uploadedImages
          };
        })
      );

      const templateConfig = {
        headerSize: formData.headerSize,
        textSize: formData.textSize,
        imagePosition: formData.imagePosition,
        heroImageSize: formData.heroImageSize,
        heroImageWidth: formData.heroImageWidth,
        heroImageHeight: formData.heroImageHeight,
        heroImageFit: formData.heroImageFit,
        heroImageAlignment: formData.heroImageAlignment,
        layoutStyle: formData.layoutStyle,
        contentWidth: formData.contentWidth,
        showTableOfContents: formData.showTableOfContents,
        tocPosition: formData.tocPosition,
        backgroundColor: formData.backgroundColor,
        contentBackgroundColor: formData.contentBackgroundColor,
        textColor: formData.textColor,
        accentColor: formData.accentColor,
        sectionSpacing: formData.sectionSpacing,
        contentPadding: formData.contentPadding,
        fontFamily: formData.fontFamily,
        headingWeight: formData.headingWeight,
        heroHeight: formData.heroHeight,
        heroOverlay: formData.heroOverlay,
        showHeroMeta: formData.showHeroMeta,
        sections: sectionsWithImages
      };

      // Convert hero image to base64 if exists
      let imageFileName = formData.imageFileName; // Keep existing filename by default
      if (formData.imageFile) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(formData.imageFile);
        });
        const base64Image = await base64Promise;
        
        // Store image via separate API call
        const uploadResponse = await fetch('/api/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            image: base64Image, 
            filename: formData.imageFile.name
          })
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          imageFileName = uploadData.filename;
        }
      }

      // Convert card image to base64 if exists
      let cardImageFileName = formData.cardImageFileName; // Keep existing filename by default
      if (formData.cardImageFile) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(formData.cardImageFile);
        });
        const base64Image = await base64Promise;
        
        // Store image via separate API call
        const uploadResponse = await fetch('/api/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            image: base64Image, 
            filename: formData.cardImageFile.name
          })
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          cardImageFileName = uploadData.filename;
        }
      }

      // Convert description image to base64 if exists
      let descriptionImageFileName = formData.descriptionImageFileName; // Keep existing filename by default
      if (formData.descriptionImageFile) {
        const reader = new FileReader();
        const base64Promise = new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(formData.descriptionImageFile);
        });
        const base64Image = await base64Promise;
        
        // Store image via separate API call
        const uploadResponse = await fetch('/api/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            image: base64Image, 
            filename: formData.descriptionImageFile.name
          })
        });
        
        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          descriptionImageFileName = uploadData.filename;
        }
      }

      const dataToSend = {
        title: formData.title,
        author: formData.author,
        category: formData.category,
        product: formData.product,
        description: formData.description,
        descriptionTextColor: formData.descriptionTextColor || '#FFFFFF',
        color: formData.color,
        imageFileName: imageFileName,
        cardImageFileName: cardImageFileName,
        descriptionImageFileName: descriptionImageFileName,
        templateConfig: JSON.stringify(templateConfig)
      };

      const url = editingId 
        ? `/api/blogs/${editingId}` 
        : '/api/blogs';
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        alert(`Blog ${editingId ? 'updated' : 'created'} successfully!`);
        resetForm();
        setShowForm(false);
        fetchBlogs();
      } else {
        const errorData = await response.json();
        alert(`Failed to save blog: ${errorData.message || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (blog) => {
    const template = blog.TemplateConfig ? JSON.parse(blog.TemplateConfig) : {};
    setFormData({
      title: blog.Title,
      author: blog.Author,
      category: blog.Category,
      product: blog.Product || 'WhiteMatter',
      description: blog.Description,
      color: blog.Color,
      imageFile: null,
      imageFileName: blog.ImageFileName,
      cardImageFile: null,
      cardImageFileName: blog.CardImageFileName,
      descriptionImageFile: null,
      descriptionImageFileName: blog.DescriptionImageFileName,
      descriptionTextColor: blog.DescriptionTextColor || '#FFFFFF',
      headerSize: template.headerSize || 'large',
      textSize: template.textSize || 'medium',
      imagePosition: template.imagePosition || 'top',
      heroImageSize: template.heroImageSize || 'medium',
      heroImageWidth: template.heroImageWidth || '100',
      heroImageHeight: template.heroImageHeight || 'auto',
      heroImageFit: template.heroImageFit || 'cover',
      heroImageAlignment: template.heroImageAlignment || 'center',
      layoutStyle: template.layoutStyle || 'default',
      contentWidth: template.contentWidth || 'standard',
      showTableOfContents: template.showTableOfContents !== undefined ? template.showTableOfContents : true,
      tocPosition: template.tocPosition || 'right',
      backgroundColor: template.backgroundColor || '#FAFAFA',
      contentBackgroundColor: template.contentBackgroundColor || '#FFFFFF',
      textColor: template.textColor || '#1D1D1F',
      accentColor: template.accentColor || '#0071e3',
      sectionSpacing: template.sectionSpacing || 'medium',
      contentPadding: template.contentPadding || 'medium',
      fontFamily: template.fontFamily || 'system',
      headingWeight: template.headingWeight || 'bold',
      heroHeight: template.heroHeight || 'medium',
      heroOverlay: template.heroOverlay || 'medium',
      showHeroMeta: template.showHeroMeta !== undefined ? template.showHeroMeta : true,
      sections: template.sections || [{ id: 1, type: 'text', title: 'Overview', content: '', fontSize: 'medium', imageFile: null, imageFileName: null, imagePosition: 'top' }]
    });
    setEditingId(blog.BlogID);
    setCurrentStep(1);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) {
      return;
    }

    try {
      const response = await fetch(`/api/blogs/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Blog deleted successfully!');
        fetchBlogs();
      } else {
        alert('Failed to delete blog. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('An error occurred. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      category: 'Technology',
      description: '',
      color: '#007AFF',
      imageFile: null,
      headerSize: 'large',
      textSize: 'medium',
      imagePosition: 'top',
      heroImageSize: 'medium',
      heroImageWidth: '100',
      heroImageHeight: 'auto',
      heroImageFit: 'cover',
      heroImageAlignment: 'center',
      layoutStyle: 'default',
      contentWidth: 'standard',
      showTableOfContents: true,
      tocPosition: 'right',
      backgroundColor: '#FAFAFA',
      contentBackgroundColor: '#FFFFFF',
      textColor: '#1D1D1F',
      accentColor: '#0071e3',
      sectionSpacing: 'medium',
      contentPadding: 'medium',
      fontFamily: 'system',
      headingWeight: 'bold',
      heroHeight: 'medium',
      heroOverlay: 'medium',
      showHeroMeta: true,
      sections: [{ id: 1, type: 'text', title: 'Overview', content: '', fontSize: 'medium', imageFile: null, imageFileName: null, imagePosition: 'top' }]
    });
    setEditingId(null);
    setCurrentStep(1);
    setShowForm(false);
  };

  const nextStep = (e) => {
    e.preventDefault();
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = (e) => {
    e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const colorOptions = [
    { value: '#007AFF', label: 'Blue' },
    { value: '#FF3B30', label: 'Red' },
    { value: '#5856D6', label: 'Purple' },
    { value: '#34C759', label: 'Green' },
    { value: '#FF9500', label: 'Orange' },
    { value: '#AF52DE', label: 'Violet' }
  ];

  const steps = [
    { number: 1, title: 'Basic Info', icon: Settings },
    { number: 2, title: 'Design', icon: Palette },
    { number: 3, title: 'Hero Section', icon: ImageIcon },
    { number: 4, title: 'Content', icon: Layers }
  ];

  if (loading) {
    return (
      <div className="admin-blogs-container">
        <div className="admin-loading">
          <div className="loading-spinner"></div>
          <p>Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="admin-blogs-container">
      <div className="admin-header">
        <div className="admin-header-left">
          <h1>Manage Blogs</h1>
          {adminUser && (
            <span className="admin-user-info">
              Welcome, {adminUser.firstName || adminUser.email}
            </span>
          )}
        </div>
        <div className="admin-header-right">
          <button 
            className="btn-add-new"
            onClick={() => setShowForm(true)}
          >
            <Plus size={20} />
            Add New Blog
          </button>
          <button 
            className="btn-logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            Logout
          </button>
        </div>
      </div>

      {showForm && (
        <motion.div 
          className="case-study-form-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <motion.div 
            className="case-study-form-card"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <div className="form-header">
              <div className="header-content">
                <h2>{editingId ? 'Edit Blog' : 'Create New Blog'}</h2>
                <p className="header-subtitle">Step {currentStep} of {totalSteps}</p>
              </div>
              <button className="btn-close" onClick={resetForm}>
                <X size={24} />
              </button>
            </div>

            {/* Progress Steps */}
            <div className="wizard-steps">
              {steps.map((step) => {
                const StepIcon = step.icon;
                return (
                  <div 
                    key={step.number}
                    className={`wizard-step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
                  >
                    <div className="step-icon">
                      <StepIcon size={20} />
                    </div>
                    <div className="step-info">
                      <span className="step-number">Step {step.number}</span>
                      <span className="step-title">{step.title}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={handleSubmit} onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}>
              <div className="form-content-wizard">
                
                {/* Step 1: Basic Info */}
                {currentStep === 1 && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key="step1"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="wizard-step-content"
                    >
                      <h3 className="step-heading">Basic Information</h3>
                      <p className="step-description">Let's start with the essential details about your case study</p>

                      <div className="form-group">
                        <label>Case Study Title *</label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          placeholder="e.g., SafePulse Continuous Authentication"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Author Name *</label>
                        <input
                          type="text"
                          value={formData.author}
                          onChange={(e) => handleInputChange('author', e.target.value)}
                          placeholder="e.g., John Doe"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Category *</label>
                        <input
                          type="text"
                          value={formData.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          placeholder="e.g., Security-SafePulse"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Product *</label>
                        <select
                          value={formData.product}
                          onChange={(e) => handleInputChange('product', e.target.value)}
                          required
                        >
                          <option value="WhiteMatter">WhiteMatter</option>
                          <option value="SafePulse">SafePulse</option>
                          <option value="TrustMark">TrustMark</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Description *</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          placeholder="Describe what this case study is about..."
                          rows="4"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Description Text Color</label>
                        <div className="color-picker-row">
                          <input
                            type="color"
                            value={formData.descriptionTextColor || '#FFFFFF'}
                            onChange={(e) => handleInputChange('descriptionTextColor', e.target.value)}
                            className="color-input"
                          />
                          <input
                            type="text"
                            value={formData.descriptionTextColor || '#FFFFFF'}
                            onChange={(e) => handleInputChange('descriptionTextColor', e.target.value)}
                            placeholder="#FFFFFF (default white)"
                            className="color-text-input"
                          />
                          {formData.descriptionTextColor && formData.descriptionTextColor !== '#FFFFFF' && (
                            <button
                              type="button"
                              className="btn-clear-color"
                              onClick={() => handleInputChange('descriptionTextColor', '#FFFFFF')}
                              title="Reset to default"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Color Theme *</label>
                        <div className="color-picker">
                          {colorOptions.map(color => (
                            <button
                              key={color.value}
                              type="button"
                              className={`color-option ${formData.color === color.value ? 'active' : ''}`}
                              style={{ backgroundColor: color.value }}
                              onClick={() => handleInputChange('color', color.value)}
                              title={color.label}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Hero Background Image (Optional)</label>
                        <div className="file-upload">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setFormData(prev => ({ ...prev, imageFile: file }));
                              }
                            }}
                            id="main-image-upload"
                          />
                          <label htmlFor="main-image-upload" className="file-upload-label">
                            <Upload size={20} />
                            {formData.imageFile ? formData.imageFile.name : (formData.imageFileName || 'Choose hero image')}
                          </label>
                          {(formData.imageFile || formData.imageFileName) && (
                            <button
                              type="button"
                              className="btn-remove-image"
                              onClick={() => setFormData(prev => ({ ...prev, imageFile: null, imageFileName: null }))}
                              title="Remove image"
                            >
                              <X size={16} /> Remove
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Card Image (For Blog Listing Page) *</label>
                        <p className="field-hint">This image will be displayed on the blogs listing page as the card thumbnail</p>
                        <div className="file-upload">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setFormData(prev => ({ ...prev, cardImageFile: file }));
                              }
                            }}
                            id="card-image-upload"
                          />
                          <label htmlFor="card-image-upload" className="file-upload-label">
                            <Upload size={20} />
                            {formData.cardImageFile ? formData.cardImageFile.name : (formData.cardImageFileName || 'Choose card image')}
                          </label>
                          {(formData.cardImageFile || formData.cardImageFileName) && (
                            <button
                              type="button"
                              className="btn-remove-image"
                              onClick={() => setFormData(prev => ({ ...prev, cardImageFile: null, cardImageFileName: null }))}
                              title="Remove image"
                            >
                              <X size={16} /> Remove
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Description Image (Optional)</label>
                        <div className="file-upload">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setFormData(prev => ({ ...prev, descriptionImageFile: file }));
                              }
                            }}
                            id="description-image-upload"
                          />
                          <label htmlFor="description-image-upload" className="file-upload-label">
                            <Upload size={20} />
                            {formData.descriptionImageFile ? formData.descriptionImageFile.name : (formData.descriptionImageFileName || 'Choose image file')}
                          </label>
                          {(formData.descriptionImageFile || formData.descriptionImageFileName) && (
                            <button
                              type="button"
                              className="btn-remove-image"
                              onClick={() => setFormData(prev => ({ ...prev, descriptionImageFile: null, descriptionImageFileName: null }))}
                              title="Remove image"
                            >
                              <X size={16} /> Remove
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}

                {/* Step 2: Design */}
                {currentStep === 2 && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key="step2"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="wizard-step-content"
                    >
                      <h3 className="step-heading">Design & Layout</h3>
                      <p className="step-description">Customize the visual appearance and layout</p>

                      <div className="form-section-card">
                        <h4>Layout Settings</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Layout Style</label>
                            <select
                              value={formData.layoutStyle}
                              onChange={(e) => handleInputChange('layoutStyle', e.target.value)}
                            >
                              <option value="default">Default</option>
                              <option value="wide">Wide</option>
                              <option value="narrow">Narrow</option>
                              <option value="full-width">Full Width</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Content Width</label>
                            <select
                              value={formData.contentWidth}
                              onChange={(e) => handleInputChange('contentWidth', e.target.value)}
                            >
                              <option value="narrow">Narrow (800px)</option>
                              <option value="standard">Standard (1200px)</option>
                              <option value="wide">Wide (1400px)</option>
                              <option value="full">Full Width</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="form-section-card">
                        <h4>Color Scheme</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Background</label>
                            <input
                              type="color"
                              value={formData.backgroundColor}
                              onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label>Content Background</label>
                            <input
                              type="color"
                              value={formData.contentBackgroundColor}
                              onChange={(e) => handleInputChange('contentBackgroundColor', e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label>Text Color</label>
                            <input
                              type="color"
                              value={formData.textColor}
                              onChange={(e) => handleInputChange('textColor', e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label>Accent Color</label>
                            <input
                              type="color"
                              value={formData.accentColor}
                              onChange={(e) => handleInputChange('accentColor', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="form-section-card">
                        <h4>Typography & Spacing</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Font Family</label>
                            <select
                              value={formData.fontFamily}
                              onChange={(e) => handleInputChange('fontFamily', e.target.value)}
                            >
                              <option value="system">System (Sans-serif)</option>
                              <option value="serif">Serif</option>
                              <option value="mono">Monospace</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Section Spacing</label>
                            <select
                              value={formData.sectionSpacing}
                              onChange={(e) => handleInputChange('sectionSpacing', e.target.value)}
                            >
                              <option value="compact">Compact</option>
                              <option value="medium">Medium</option>
                              <option value="spacious">Spacious</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}

                {/* Step 3: Hero Section */}
                {currentStep === 3 && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key="step3"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="wizard-step-content"
                    >
                      <h3 className="step-heading">Hero Section</h3>
                      <p className="step-description">Configure the header image and hero appearance</p>

                      <div className="form-group">
                        <label>Header Image</label>
                        <div className="file-upload">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange('imageFile', e.target.files[0])}
                            id="image-upload"
                          />
                          <label htmlFor="image-upload" className="file-upload-label">
                            <Upload size={20} />
                            {formData.imageFile ? formData.imageFile.name : 'Choose image file'}
                          </label>
                        </div>
                      </div>

                      <div className="form-section-card">
                        <h4>Hero Settings</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Header Size</label>
                            <select
                              value={formData.headerSize}
                              onChange={(e) => handleInputChange('headerSize', e.target.value)}
                            >
                              <option value="small">Small</option>
                              <option value="medium">Medium</option>
                              <option value="large">Large</option>
                              <option value="xlarge">Extra Large</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Hero Height</label>
                            <select
                              value={formData.heroHeight}
                              onChange={(e) => handleInputChange('heroHeight', e.target.value)}
                            >
                              <option value="small">Small</option>
                              <option value="medium">Medium</option>
                              <option value="large">Large</option>
                              <option value="xlarge">Extra Large</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Hero Overlay</label>
                            <select
                              value={formData.heroOverlay}
                              onChange={(e) => handleInputChange('heroOverlay', e.target.value)}
                            >
                              <option value="none">None</option>
                              <option value="light">Light</option>
                              <option value="medium">Medium</option>
                              <option value="dark">Dark</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="form-section-card">
                        <h4>Hero Image Sizing</h4>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Image Size</label>
                            <select
                              value={formData.heroImageSize}
                              onChange={(e) => handleInputChange('heroImageSize', e.target.value)}
                            >
                              <option value="small">Small (33%)</option>
                              <option value="medium">Medium (50%)</option>
                              <option value="large">Large (75%)</option>
                              <option value="full">Full Width (100%)</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>Image Fit</label>
                            <select
                              value={formData.heroImageFit}
                              onChange={(e) => handleInputChange('heroImageFit', e.target.value)}
                            >
                              <option value="cover">Cover</option>
                              <option value="contain">Contain</option>
                              <option value="fill">Fill</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                )}

                {/* Step 4: Content Sections */}
                {currentStep === 4 && (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key="step4"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="wizard-step-content"
                    >
                      <h3 className="step-heading">Content Sections</h3>
                      <p className="step-description">Add and customize content sections for your case study</p>

                      {formData.sections.map((section, index) => (
                        <div key={section.id} className="content-section-card">
                          <div className="section-card-header">
                            <h4>Section {index + 1}</h4>
                            <div className="section-controls">
                              {index > 0 && (
                                <button
                                  type="button"
                                  className="btn-move-section"
                                  onClick={() => moveSection(index, 'up')}
                                  title="Move up"
                                >
                                  <ChevronUp size={16} />
                                </button>
                              )}
                              {index < formData.sections.length - 1 && (
                                <button
                                  type="button"
                                  className="btn-move-section"
                                  onClick={() => moveSection(index, 'down')}
                                  title="Move down"
                                >
                                  <ChevronDown size={16} />
                                </button>
                              )}
                              {formData.sections.length > 1 && (
                                <button
                                  type="button"
                                  className="btn-remove-section-small"
                                  onClick={() => removeSection(section.id)}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Section Title</label>
                            <input
                              type="text"
                              value={section.title}
                              onChange={(e) => updateSection(section.id, 'title', e.target.value)}
                              placeholder="e.g., Overview, Challenge, Solution"
                            />
                          </div>

                          <div className="form-group">
                            <label>Title Background Color</label>
                            <div className="color-picker-row">
                              <input
                                type="color"
                                value={section.titleBackgroundColor || 'transparent'}
                                onChange={(e) => updateSection(section.id, 'titleBackgroundColor', e.target.value)}
                                className="color-input"
                              />
                              <input
                                type="text"
                                value={section.titleBackgroundColor || 'transparent'}
                                onChange={(e) => updateSection(section.id, 'titleBackgroundColor', e.target.value)}
                                placeholder="transparent or #RRGGBB"
                                className="color-text-input"
                              />
                              {section.titleBackgroundColor && section.titleBackgroundColor !== 'transparent' && (
                                <button
                                  type="button"
                                  className="btn-clear-color"
                                  onClick={() => updateSection(section.id, 'titleBackgroundColor', 'transparent')}
                                  title="Clear background"
                                >
                                  <X size={16} />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Title Text Color</label>
                            <div className="color-picker-row">
                              <input
                                type="color"
                                value={section.titleTextColor || '#1D1D1F'}
                                onChange={(e) => updateSection(section.id, 'titleTextColor', e.target.value)}
                                className="color-input"
                              />
                              <input
                                type="text"
                                value={section.titleTextColor || '#1D1D1F'}
                                onChange={(e) => updateSection(section.id, 'titleTextColor', e.target.value)}
                                placeholder="#1D1D1F (default)"
                                className="color-text-input"
                              />
                              {section.titleTextColor && section.titleTextColor !== '#1D1D1F' && (
                                <button
                                  type="button"
                                  className="btn-clear-color"
                                  onClick={() => updateSection(section.id, 'titleTextColor', '#1D1D1F')}
                                  title="Reset to default"
                                >
                                  <X size={16} />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Content</label>
                            <div className="text-editor-toolbar">
                              <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => insertFormatting(section.id, 'bold')}
                                title="Bold (wrap with **text**)"
                              >
                                <Bold size={16} />
                              </button>
                              <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => insertFormatting(section.id, 'italic')}
                                title="Italic (wrap with *text*)"
                              >
                                <Italic size={16} />
                              </button>
                              <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => insertFormatting(section.id, 'bullet')}
                                title="Bullet points"
                              >
                                <List size={16} />
                              </button>
                              <button
                                type="button"
                                className="toolbar-btn"
                                onClick={() => insertFormatting(section.id, 'link')}
                                title="Add link"
                              >
                                <LinkIcon size={16} />
                              </button>
                              <span className="toolbar-hint">Select text and click to format</span>
                            </div>
                            <textarea
                              id={`content-${section.id}`}
                              value={section.content}
                              onChange={(e) => updateSection(section.id, 'content', e.target.value)}
                              placeholder="Enter section content...\n\nFormatting:\n**bold text**\n*italic text*\n• Bullet point\n[link text](url)"
                              rows="6"
                            />
                          </div>

                          <div className="form-group">
                            <label>Section Image (Optional)</label>
                            <div className="file-upload">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => updateSectionImage(section.id, e.target.files[0])}
                                id={`section-image-${section.id}`}
                              />
                              <label htmlFor={`section-image-${section.id}`} className="file-upload-label">
                                <Upload size={20} />
                                {section.imageFile ? section.imageFile.name : (section.imageFileName || 'Choose image file')}
                              </label>
                              {(section.imageFile || section.imageFileName) && (
                                <button
                                  type="button"
                                  className="btn-remove-image"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      sections: prev.sections.map(s =>
                                        s.id === section.id
                                          ? { ...s, imageFile: null, imageFileName: null }
                                          : s
                                      )
                                    }));
                                  }}
                                  title="Remove image"
                                >
                                  <X size={16} /> Remove
                                </button>
                              )}
                            </div>
                          </div>

                          {(section.imageFile || section.imageFileName) && (
                            <>
                              <div className="form-group">
                                <label>Image Caption (Optional)</label>
                                <input
                                  type="text"
                                  value={section.imageCaption || ''}
                                  onChange={(e) => updateSection(section.id, 'imageCaption', e.target.value)}
                                  placeholder="e.g., Figure 1: Video conference call screenshot"
                                />
                              </div>

                              <div className="form-group">
                                <label>Image Text (Optional)</label>
                                <div className="formatting-toolbar">
                                  <button type="button" onClick={() => insertImageTextFormatting(section.id, 'bold')} title="Bold">
                                    <Bold size={16} />
                                  </button>
                                  <button type="button" onClick={() => insertImageTextFormatting(section.id, 'italic')} title="Italic">
                                    <Italic size={16} />
                                  </button>
                                  <button type="button" onClick={() => insertImageTextFormatting(section.id, 'bullet')} title="Bullet Point">
                                    <List size={16} />
                                  </button>
                                  <button type="button" onClick={() => insertImageTextFormatting(section.id, 'link')} title="Add Link">
                                    <LinkIcon size={16} />
                                  </button>
                                </div>
                                <textarea
                                  id={`imageText-${section.id}`}
                                  value={section.imageText || ''}
                                  onChange={(e) => updateSection(section.id, 'imageText', e.target.value)}
                                  placeholder="Add descriptive text or explanation below the image... Supports **bold**, *italic*, [links](url), and • bullets"
                                  rows={3}
                                />
                              </div>

                              <div className="form-group">
                                <label>Image Positioning</label>
                                <div className="positioning-controls">
                                  <div className="position-control-group">
                                    <label>Move Horizontal: {section.imageOffsetX || 0}px</label>
                                    <input
                                      type="range"
                                      min="-500"
                                      max="500"
                                      value={section.imageOffsetX || 0}
                                      onChange={(e) => updateSection(section.id, 'imageOffsetX', parseInt(e.target.value))}
                                      className="position-slider"
                                    />
                                  </div>

                                  <div className="position-control-group">
                                    <label>Move Vertical: {section.imageOffsetY || 0}px</label>
                                    <input
                                      type="range"
                                      min="-500"
                                      max="500"
                                      value={section.imageOffsetY || 0}
                                      onChange={(e) => updateSection(section.id, 'imageOffsetY', parseInt(e.target.value))}
                                      className="position-slider"
                                    />
                                  </div>

                                  <div className="position-control-group">
                                    <label>Width: {section.imageCustomWidth || 100}%</label>
                                    <input
                                      type="range"
                                      min="10"
                                      max="200"
                                      value={section.imageCustomWidth || 100}
                                      onChange={(e) => updateSection(section.id, 'imageCustomWidth', parseInt(e.target.value))}
                                      className="position-slider"
                                    />
                                  </div>

                                  <div className="position-control-group">
                                    <label>Space Below: {section.imageSpaceBelow !== undefined ? section.imageSpaceBelow : 32}px</label>
                                    <input
                                      type="range"
                                      min="-100"
                                      max="200"
                                      value={section.imageSpaceBelow !== undefined ? section.imageSpaceBelow : 32}
                                      onChange={(e) => updateSection(section.id, 'imageSpaceBelow', parseInt(e.target.value))}
                                      className="position-slider"
                                    />
                                  </div>
                                </div>

                                {/* Live Preview */}
                                <div className="image-preview-container">
                                  <label>Live Preview:</label>
                                  <div className="image-preview-wrapper">
                                    <div 
                                      className="preview-image-positioned"
                                      style={{
                                        marginLeft: `${section.imageOffsetX || 0}px`,
                                        marginTop: `${section.imageOffsetY || 0}px`,
                                        marginBottom: `${section.imageSpaceBelow !== undefined ? section.imageSpaceBelow : 32}px`,
                                        width: `${section.imageCustomWidth || 100}%`,
                                        maxWidth: '100%'
                                      }}
                                    >
                                      {section.imageFile ? (
                                        <img 
                                          src={URL.createObjectURL(section.imageFile)} 
                                          alt="Preview"
                                          className="preview-image"
                                        />
                                      ) : section.imageFileName ? (
                                        <img 
                                          src={`/uploads/${section.imageFileName}`} 
                                          alt="Preview"
                                          className="preview-image"
                                        />
                                      ) : null}
                                      {section.imageCaption && (
                                        <p className="preview-image-caption">{section.imageCaption}</p>
                                      )}
                                      {section.imageText && (
                                        <div className="preview-image-text">{section.imageText}</div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </>
                          )}

                          {/* Multiple Additional Images */}
                          <div className="form-group">
                            <label>Additional Images (Optional)</label>
                            <p className="field-hint">Add multiple images to this section</p>
                            
                            {section.images && section.images.length > 0 && (
                              <div className="multiple-images-list">
                                {section.images.map((image, imgIndex) => (
                                  <div key={image.id} className="image-item-card">
                                    <div className="image-item-header">
                                      <span className="image-item-number">Image {imgIndex + 1}</span>
                                      <button
                                        type="button"
                                        className="btn-remove-image-small"
                                        onClick={() => removeImageFromSection(section.id, image.id)}
                                        title="Remove this image"
                                      >
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <div className="image-item-preview">
                                      {image.file && (
                                        <span className="image-filename">{image.file.name}</span>
                                      )}
                                      {image.fileName && !image.file && (
                                        <span className="image-filename">{image.fileName}</span>
                                      )}
                                    </div>
                                    <div className="image-item-controls">
                                      <div className="form-group-inline">
                                        <label>Position</label>
                                        <select
                                          value={image.imagePosition || 'top'}
                                          onChange={(e) => updateSectionImagePosition(section.id, image.id, e.target.value)}
                                          className="image-position-select"
                                        >
                                          <option value="top">Top</option>
                                          <option value="bottom">Bottom</option>
                                          <option value="top-left">Top Left</option>
                                          <option value="center-left">Center Left</option>
                                          <option value="bottom-left">Bottom Left</option>
                                          <option value="top-right">Top Right</option>
                                          <option value="center-right">Center Right</option>
                                          <option value="bottom-right">Bottom Right</option>
                                        </select>
                                      </div>

                                      <div className="positioning-controls">
                                        <div className="position-control-group">
                                          <label>Move Horizontal: {image.offsetX || 0}px</label>
                                          <input
                                            type="range"
                                            min="-500"
                                            max="500"
                                            value={image.offsetX || 0}
                                            onChange={(e) => updateImageProperty(section.id, image.id, 'offsetX', parseInt(e.target.value))}
                                            className="position-slider"
                                          />
                                        </div>

                                        <div className="position-control-group">
                                          <label>Move Vertical: {image.offsetY || 0}px</label>
                                          <input
                                            type="range"
                                            min="-500"
                                            max="500"
                                            value={image.offsetY || 0}
                                            onChange={(e) => updateImageProperty(section.id, image.id, 'offsetY', parseInt(e.target.value))}
                                            className="position-slider"
                                          />
                                        </div>

                                        <div className="position-control-group">
                                          <label>Width: {image.customWidth || 100}%</label>
                                          <input
                                            type="range"
                                            min="10"
                                            max="200"
                                            value={image.customWidth || 100}
                                            onChange={(e) => updateImageProperty(section.id, image.id, 'customWidth', parseInt(e.target.value))}
                                            className="position-slider"
                                          />
                                        </div>

                                        <div className="position-control-group">
                                          <label>Space Below: {image.spaceBelow !== undefined ? image.spaceBelow : 32}px</label>
                                          <input
                                            type="range"
                                            min="-100"
                                            max="200"
                                            value={image.spaceBelow !== undefined ? image.spaceBelow : 32}
                                            onChange={(e) => updateImageProperty(section.id, image.id, 'spaceBelow', parseInt(e.target.value))}
                                            className="position-slider"
                                          />
                                        </div>
                                      </div>

                                      {/* Live Preview for Additional Image */}
                                      <div className="image-preview-container-small">
                                        <label>Preview:</label>
                                        <div className="image-preview-wrapper-small">
                                          <div 
                                            className="preview-image-positioned"
                                            style={{
                                              marginLeft: `${image.offsetX || 0}px`,
                                              marginTop: `${image.offsetY || 0}px`,
                                              marginBottom: `${image.spaceBelow !== undefined ? image.spaceBelow : 32}px`,
                                              width: `${image.customWidth || 100}%`,
                                              maxWidth: '100%'
                                            }}
                                          >
                                            {image.file ? (
                                              <img 
                                                src={URL.createObjectURL(image.file)} 
                                                alt="Preview"
                                                className="preview-image-small"
                                              />
                                            ) : image.fileName ? (
                                              <img 
                                                src={`/uploads/${image.fileName}`} 
                                                alt="Preview"
                                                className="preview-image-small"
                                              />
                                            ) : null}
                                            {image.caption && (
                                              <p className="preview-image-caption">{image.caption}</p>
                                            )}
                                            {image.imageText && (
                                              <div className="preview-image-text">{image.imageText}</div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    <input
                                      type="text"
                                      value={image.caption || ''}
                                      onChange={(e) => updateSectionImageCaption(section.id, image.id, e.target.value)}
                                      placeholder="Caption for this image"
                                      className="image-caption-input"
                                    />
                                    <div className="image-text-formatting" style={{ marginTop: '8px' }}>
                                      <div className="formatting-toolbar-small">
                                        <button type="button" onClick={() => insertImageTextFormatting(section.id, 'bold', image.id)} title="Bold">
                                          <Bold size={14} />
                                        </button>
                                        <button type="button" onClick={() => insertImageTextFormatting(section.id, 'italic', image.id)} title="Italic">
                                          <Italic size={14} />
                                        </button>
                                        <button type="button" onClick={() => insertImageTextFormatting(section.id, 'bullet', image.id)} title="Bullet Point">
                                          <List size={14} />
                                        </button>
                                        <button type="button" onClick={() => insertImageTextFormatting(section.id, 'link', image.id)} title="Add Link">
                                          <LinkIcon size={14} />
                                        </button>
                                      </div>
                                      <textarea
                                        id={`imageText-${section.id}-${image.id}`}
                                        value={image.imageText || ''}
                                        onChange={(e) => updateImageProperty(section.id, image.id, 'imageText', e.target.value)}
                                        placeholder="Add text below this image... Supports **bold**, *italic*, [links](url), • bullets"
                                        rows={3}
                                        className="image-text-input"
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="file-upload">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    addImageToSection(section.id, e.target.files[0]);
                                    e.target.value = '';
                                  }
                                }}
                                id={`add-image-${section.id}`}
                              />
                              <label htmlFor={`add-image-${section.id}`} className="file-upload-label-secondary">
                                <Plus size={18} />
                                Add Image
                              </label>
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Font Size</label>
                              <select
                                value={section.fontSize}
                                onChange={(e) => updateSection(section.id, 'fontSize', e.target.value)}
                              >
                                <option value="small">Small</option>
                                <option value="medium">Medium</option>
                                <option value="large">Large</option>
                              </select>
                            </div>

                            <div className="form-group">
                              <label>Text Alignment</label>
                              <select
                                value={section.textAlign || 'left'}
                                onChange={(e) => updateSection(section.id, 'textAlign', e.target.value)}
                              >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                              </select>
                            </div>

                            <div className="form-group">
                              <label>Line Spacing</label>
                              <select
                                value={section.lineSpacing || 'normal'}
                                onChange={(e) => updateSection(section.id, 'lineSpacing', e.target.value)}
                              >
                                <option value="compact">Compact (1.4)</option>
                                <option value="normal">Normal (1.7)</option>
                                <option value="relaxed">Relaxed (2.0)</option>
                                <option value="loose">Loose (2.5)</option>
                              </select>
                            </div>

                            <div className="form-group">
                              <label>Image Position</label>
                              <select
                                value={section.imagePosition || 'top'}
                                onChange={(e) => updateSection(section.id, 'imagePosition', e.target.value)}
                              >
                                <option value="top">Top</option>
                                <option value="bottom">Bottom</option>
                                <option value="top-left">Top Left</option>
                                <option value="center-left">Center Left</option>
                                <option value="bottom-left">Bottom Left</option>
                                <option value="top-right">Top Right</option>
                                <option value="center-right">Center Right</option>
                                <option value="bottom-right">Bottom Right</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn-add-section-wizard"
                        onClick={addSection}
                      >
                        <Plus size={18} />
                        Add Section
                      </button>
                    </motion.div>
                  </AnimatePresence>
                )}

              </div>

              {/* Wizard Navigation */}
              <div className="wizard-navigation">
                {currentStep > 1 && (
                  <button
                    type="button"
                    className="btn-wizard-prev"
                    onClick={prevStep}
                  >
                    <ArrowLeft size={18} />
                    Previous
                  </button>
                )}

                <button
                  type="button"
                  className="btn-wizard-preview"
                  onClick={() => setShowPreview(true)}
                >
                  <Eye size={18} />
                  Preview
                </button>

                <div className="wizard-nav-spacer"></div>

                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    className="btn-wizard-next"
                    onClick={nextStep}
                  >
                    Next
                    <ArrowLeft size={18} style={{ transform: 'rotate(180deg)' }} />
                  </button>
                ) : (
                  <button type="submit" className="btn-wizard-submit" disabled={loading}>
                    <Save size={18} />
                    {loading ? 'Saving...' : (editingId ? 'Update' : 'Create')}
                  </button>
                )}
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Preview Mode */}
      {showPreview && (
        <motion.div 
          className="preview-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="preview-container">
            <div className="preview-header">
              <button className="btn-back" onClick={() => setShowPreview(false)}>
                <ArrowLeft size={20} />
                Back to Edit
              </button>
              <h2>Preview Mode</h2>
              <button className="btn-publish" onClick={handleSubmit}>
                <Save size={20} />
                Publish
              </button>
            </div>

            <div className="preview-content">
              {/* Hero Section Preview */}
              <div 
                className={`preview-hero header-${formData.headerSize}`}
                style={{ backgroundColor: formData.color }}
              >
                <div className="preview-hero-content">
                  <div className="preview-meta">
                    <span>{formData.author}</span>
                    <span>{formData.category}</span>
                  </div>
                  <h1 contentEditable onBlur={(e) => handleInputChange('title', e.target.textContent)}>
                    {formData.title || 'Enter Title'}
                  </h1>
                  <p contentEditable onBlur={(e) => handleInputChange('description', e.target.textContent)}>
                    {formData.description || 'Enter Description'}
                  </p>
                </div>
              </div>

              {/* Main Content Preview */}
              <div className={`preview-main image-position-${formData.imagePosition}`}>
                {/* Header Image */}
                {(formData.imageFile || formData.imageFileName) && (
                  <div className="preview-header-image">
                    <img 
                      src={formData.imageFile ? URL.createObjectURL(formData.imageFile) : `/uploads/${formData.imageFileName}`} 
                      alt="Header"
                    />
                    <div className="image-position-controls">
                      <label>Header Image Position:</label>
                      <select 
                        value={formData.imagePosition}
                        onChange={(e) => handleInputChange('imagePosition', e.target.value)}
                      >
                        <option value="top">Top</option>
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="bottom">Bottom</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Sections Preview */}
                <div className="preview-sections">
                  {formData.sections.map((section, index) => (
                    <div 
                      key={section.id} 
                      className={`preview-section section-image-${section.imagePosition || 'top'}`}
                    >
                      <div className="section-controls">
                        <span>Section {index + 1}</span>
                        {(section.imageFile || section.imageFileName) && (
                          <select
                            value={section.imagePosition || 'top'}
                            onChange={(e) => updateSection(section.id, 'imagePosition', e.target.value)}
                          >
                            <option value="top">Image Top</option>
                            <option value="left">Image Left</option>
                            <option value="right">Image Right</option>
                            <option value="bottom">Image Bottom</option>
                          </select>
                        )}
                      </div>

                      {/* Section Image - Top */}
                      {(section.imageFile || section.imageFileName) && section.imagePosition === 'top' && (
                        <div className="preview-section-image">
                          <img src={section.imageFile ? URL.createObjectURL(section.imageFile) : `/uploads/${section.imageFileName}`} alt={section.title} />
                        </div>
                      )}

                      <div className="preview-section-content">
                        {/* Section Image - Left */}
                        {(section.imageFile || section.imageFileName) && section.imagePosition === 'left' && (
                          <div className="preview-section-image image-left">
                            <img src={section.imageFile ? URL.createObjectURL(section.imageFile) : `/uploads/${section.imageFileName}`} alt={section.title} />
                          </div>
                        )}

                        <div className="preview-section-text">
                          <h2 
                            contentEditable 
                            onBlur={(e) => updateSection(section.id, 'title', e.target.textContent)}
                            className={`font-${section.fontSize}`}
                          >
                            {section.title || 'Enter Section Title'}
                          </h2>
                          <div 
                            contentEditable 
                            onBlur={(e) => updateSection(section.id, 'content', e.target.textContent)}
                            className={`preview-text font-${section.fontSize}`}
                          >
                            {section.content || 'Enter section content...'}
                          </div>
                        </div>

                        {/* Section Image - Right */}
                        {(section.imageFile || section.imageFileName) && section.imagePosition === 'right' && (
                          <div className="preview-section-image image-right">
                            <img src={section.imageFile ? URL.createObjectURL(section.imageFile) : `/uploads/${section.imageFileName}`} alt={section.title} />
                          </div>
                        )}
                      </div>

                      {/* Section Image - Bottom */}
                      {(section.imageFile || section.imageFileName) && section.imagePosition === 'bottom' && (
                        <div className="preview-section-image">
                          <img src={section.imageFile ? URL.createObjectURL(section.imageFile) : `/uploads/${section.imageFileName}`} alt={section.title} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      <div className="blogs-list">
        {!Array.isArray(blogs) || blogs.length === 0 ? (
          <div className="empty-state">
            <Shield size={64} />
            <h3>No blogs yet</h3>
            <p>Click "Add New Blog" to create your first blog</p>
          </div>
        ) : (
          <div className="blogs-grid">
            {blogs.map((blog) => (
              <motion.div
                key={blog.BlogID}
                className="blog-item"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="blog-header" style={{ borderLeftColor: blog.Color }}>
                  <h3>{blog.Title}</h3>
                  <div className="blog-actions">
                    <button 
                      className="btn-icon"
                      onClick={() => handleEdit(blog)}
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button 
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(blog.BlogID)}
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <div className="blog-details">
                  <p className="author">By {blog.Author}</p>
                  <p className="category">{blog.Category}</p>
                  <p className="description">{blog.Description}</p>
                  {blog.ImageFileName && (
                    <span className="file-badge">📷 Image</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBlogs;
